import { AttachmentsPanel } from "@/components/accounting/attachments";
import { Button } from "@/components/ui/button";
import { EntryStatusBadge } from "@/components/accounting/entry-status-badge";
import { MoneyDisplay } from "@/components/accounting/money-display";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	useGetEntry,
	useListEntries,
	useReverseEntry,
} from "@/hooks/use-accounting";
import { useAccountingShortcuts } from "@/hooks/use-accounting-shortcuts";
import { formatDate } from "@/lib/format";
import { pushRecentItem } from "@/lib/recent-accounting";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/accounting/journal/$id")({
	component: JournalEntryDetailPage,
});

function JournalEntryDetailPage() {
	const { t } = useTranslation("accounting");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const { data: entry, isLoading } = useGetEntry(id);
	const reverseEntry = useReverseEntry();
	const [reversing, setReversing] = useState(false);

	useBreadcrumbs([
		{ label: t("journal"), href: "/accounting/journal" },
		{ label: entry?.entryNumber || id },
	]);

	// Track as recent item
	useEffect(() => {
		if (entry) {
			pushRecentItem({
				type: "entry",
				id: entry.id,
				label: `${entry.entryNumber} ${entry.description || ""}`.trim(),
				path: `/accounting/journal/${entry.id}`,
			});
		}
	}, [entry?.id]);

	// Prev/next entry navigation
	const { data: entriesData } = useListEntries({});
	const entryIds = useMemo(
		() => (entriesData?.entries ?? []).map((e) => e.id),
		[entriesData],
	);
	const currentIdx = entryIds.indexOf(id);
	const prevId = currentIdx > 0 ? entryIds[currentIdx - 1] : null;
	const nextId =
		currentIdx >= 0 && currentIdx < entryIds.length - 1
			? entryIds[currentIdx + 1]
			: null;

	useAccountingShortcuts(
		useMemo(
			() => ({
				"[": () => {
					if (prevId)
						navigate({
							to: "/accounting/journal/$id",
							params: { id: prevId },
						});
				},
				"]": () => {
					if (nextId)
						navigate({
							to: "/accounting/journal/$id",
							params: { id: nextId },
						});
				},
				Escape: () => navigate({ to: "/accounting/journal" }),
			}),
			[prevId, nextId, navigate],
		),
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() =>
			entry
				? [
						{
							id: "number",
							content: `${t("entry_number")}: ${entry.entryNumber}`,
							align: "left" as const,
						},
					]
				: [],
		[entry, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (!entry) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-sm text-muted-foreground">{t("no_entries")}</p>
			</div>
		);
	}

	const basisKey = `basis_${entry.basis}` as const;
	const categoryKey = `category_${entry.category}` as const;
	const originKey = `origin_${entry.origin}` as const;

	const totalDebit = entry.lines
		.filter((l) => l.side === 1)
		.reduce((sum, l) => sum + Number.parseFloat(l.amount || "0"), 0);
	const totalCredit = entry.lines
		.filter((l) => l.side === 2)
		.reduce((sum, l) => sum + Number.parseFloat(l.amount || "0"), 0);
	const entryLineCols = useMemo<ColumnDef<(typeof entry.lines)[number]>[]>(
		() => [
			{
				accessorKey: "accountCode",
				header: t("line_account"),
				cell: ({ row }) => (
					<span className="font-mono text-xs">
						{row.original.accountCode || "—"}
					</span>
				),
			},
			{
				accessorKey: "description",
				header: t("line_description"),
				cell: ({ row }) => row.original.description || "—",
			},
			{
				id: "debit",
				header: () => (
					<span className="block text-right">{t("line_debit")}</span>
				),
				cell: ({ row }) =>
					row.original.side === 1 ? (
						<span className="block text-right">
							<MoneyDisplay
								amount={row.original.amount}
								currency={entry.currency}
							/>
						</span>
					) : null,
			},
			{
				id: "credit",
				header: () => (
					<span className="block text-right">{t("line_credit")}</span>
				),
				cell: ({ row }) =>
					row.original.side === 2 ? (
						<span className="block text-right">
							<MoneyDisplay
								amount={row.original.amount}
								currency={entry.currency}
							/>
						</span>
					) : null,
			},
		],
		[entry.currency, t],
	);

	function handleReverse() {
		setReversing(true);
		reverseEntry.mutate(
			{ id: entry!.id },
			{ onSettled: () => setReversing(false) },
		);
	}

	return (
		<div className="space-y-4 p-3">
			<div className="flex items-center gap-2">
				{prevId && (
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() =>
							navigate({
								to: "/accounting/journal/$id",
								params: { id: prevId },
							})
						}
					>
						←
					</Button>
				)}
				{nextId && (
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() =>
							navigate({
								to: "/accounting/journal/$id",
								params: { id: nextId },
							})
						}
					>
						→
					</Button>
				)}
				{entryIds.length > 0 && (
					<span className="text-xs text-muted-foreground">
						{currentIdx + 1} / {entryIds.length}
					</span>
				)}
				<div className="flex-1" />
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						navigate({ to: "/accounting/journal/new" });
						toast.info(
							t("clone_hint", {
								defaultValue: "Создайте проводку на основе шаблона",
							}),
						);
					}}
				>
					{t("action_clone", { defaultValue: "Дублировать" })}
				</Button>
				{entry?.status === 3 && (
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() => setReversing(true)}
					>
						{t("toolbar_reverse")}
					</Button>
				)}
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_export")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => {
								if (!entry) return;
								const rows = entry.lines.map(
									(l) =>
										`${l.accountCode},${l.description ?? ""},${l.side === 1 ? l.amount : ""},${l.side === 2 ? l.amount : ""}`,
								);
								const csv = `${t("line_account")},${t("line_description")},${t("line_debit")},${t("line_credit")}\n${rows.join("\n")}`;
								const blob = new Blob([csv], { type: "text/csv" });
								const url = URL.createObjectURL(blob);
								const a = document.createElement("a");
								a.href = url;
								a.download = `${entry.entryNumber}.csv`;
								a.click();
								URL.revokeObjectURL(url);
							}}
						>
							CSV
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="font-mono text-sm font-semibold">
						{entry.entryNumber}
					</span>
					<EntryStatusBadge status={entry.status} />
				</div>

				{entry.status === 3 && (
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="gap-2 text-destructive hover:bg-destructive/10"
							>
								<RotateCcw className="h-3.5 w-3.5" />
								{t("action_reverse")}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>{t("action_reverse")}</AlertDialogTitle>
								<AlertDialogDescription>
									{t("confirm_reverse", { number: entry.entryNumber })}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>
									{t("cancel", { ns: "common" })}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={handleReverse}
									disabled={reversing}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{t("action_reverse")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}
			</div>

			<div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_date")}</p>
					<p className="text-sm font-medium">
						{entry.validDate ? formatDate(entry.validDate.toDate()) : "—"}
					</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_period")}</p>
					<p className="text-sm font-medium">{entry.periodId || "—"}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_basis")}</p>
					<p className="text-sm font-medium">{t(basisKey)}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_category")}</p>
					<Badge variant="secondary">{t(categoryKey)}</Badge>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_currency")}</p>
					<p className="text-sm font-medium">{entry.currency || "—"}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">{t("entry_origin")}</p>
					<Badge variant="outline">{t(originKey)}</Badge>
				</div>
				{entry.confidence > 0 && entry.confidence < 1 && (
					<div>
						<p className="text-xs text-muted-foreground">
							{t("entry_confidence")}
						</p>
						<p className="text-sm font-medium">
							{Math.round(entry.confidence * 100)}%
						</p>
					</div>
				)}
			</div>

			{entry.description && (
				<div>
					<p className="text-xs text-muted-foreground mb-1">
						{t("entry_description")}
					</p>
					<p className="text-sm">{entry.description}</p>
				</div>
			)}

			<DataTable
				dense
				columns={entryLineCols}
				data={entry.lines}
				getRowId={(row) => row.accountCode || row.accountId}
				footerRow={{
					accountCode: {
						content: t("balance_indicator"),
						colSpan: 2,
					},
					debit: (
						<MoneyDisplay
							amount={String(totalDebit)}
							currency={entry.currency}
						/>
					),
					credit: (
						<MoneyDisplay
							amount={String(totalCredit)}
							currency={entry.currency}
						/>
					),
				}}
			/>

			<AttachmentsPanel entityType="journal_entry" entityId={entry.id} />
		</div>
	);
}
